import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Divider,
    Avatar,
    Tooltip,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Timeline as TimelineIcon,
    PieChart as PieChartIcon,
    Assessment as AssessmentIcon,
    Deck as DeckIcon,
    SportsEsports as SportsEsportsIcon,
    Palette as PaletteIcon,
    PlayArrow as PlayArrowIcon,
    Shuffle as ShuffleIcon,
    Person as PersonIcon,
    EmojiEvents as EmojiEventsIcon,
    Psychology as PsychologyIcon,
    Analytics as AnalyticsIcon,
    ShowChart as ShowChartIcon,
    SwapHoriz as SwapHorizIcon,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import { InkColorIcons } from './InkColorIcon';
import { getUserMatchesWithDetails, getAllArchetypes, getAggregatedArchetypeStats } from '../../services/tracker-data';
import { useAuth } from '../../contexts/AuthContext';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import {
    MIN_GAMES_FOR_INSIGHT,
    MIN_GAMES_RELIABLE,
    MIN_GAMES_STRONG,
    getWinrateColor,
    formatWinrate,
    getLastNWinrate,
    calculateArchetypeMatchups,
    calculateRollingWinrate,
    calculateMonthlyTrends
} from './user-stats-utils';

// Dashboard szybkich wniosków dla gracza (TCG)
function QuickInsightsDashboard({ stats, matches = [], loading = false }) {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const overall = stats?.overall || null;
    const perArchetype = stats?.perArchetype || {};
    const perDeck = stats?.perDeck || {};

    const last10 = getLastNWinrate(matches, 10);
    const last50 = getLastNWinrate(matches, 50);
    const last100 = getLastNWinrate(matches, 100);

    // Przewaga Play vs Draw
    const playWR = overall?.play?.winrate ?? 0;
    const drawWR = overall?.draw?.winrate ?? 0;
    const playGames = overall?.play?.games ?? 0;
    const drawGames = overall?.draw?.games ?? 0;
    const playDrawAdvantage = playGames >= 3 || drawGames >= 3
        ? (playWR >= drawWR ? { side: 'play', value: playWR, diff: playWR - drawWR }
            : { side: 'draw', value: drawWR, diff: drawWR - playWR })
        : null;

    // Najlepszy/najgorszy archetyp (min MIN_GAMES_FOR_INSIGHT)
    const archetypeArray = Object.entries(perArchetype)
        .map(([id, d]) => ({ id, ...d }))
        .filter(a => a.games >= MIN_GAMES_FOR_INSIGHT)
        .sort((a, b) => (b.winrate ?? 0) - (a.winrate ?? 0));
    const bestArchetype = archetypeArray[0];
    const worstArchetype = archetypeArray.length > 1 ? archetypeArray[archetypeArray.length - 1] : null;

    // Najlepsza/najgorsza talia (min MIN_GAMES_FOR_INSIGHT)
    const deckArray = Object.entries(perDeck)
        .map(([id, d]) => ({ id, ...d }))
        .filter(d => d.games >= MIN_GAMES_FOR_INSIGHT)
        .sort((a, b) => (b.winrate ?? 0) - (a.winrate ?? 0));
    const bestDeck = deckArray[0];
    const worstDeck = deckArray.length > 1 ? deckArray[deckArray.length - 1] : null;

    const totalGames = overall?.games ?? 0;
    const hasEnoughData = totalGames >= 10;

    if (loading || !stats) return null;

    return (
        <Card sx={{
            background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.2) 0%, rgba(17, 24, 39, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(76, 29, 149, 0.35)',
            borderRadius: '12px',
            mb: 2
        }}>
            <CardContent sx={{ p: isSmallMobile ? 1.5 : 2 }}>
                {/* Kompaktowy pasek KPI na górze */}
                {totalGames > 0 && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: isSmallMobile ? 1.5 : 2,
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: '1px solid rgba(76, 29, 149, 0.2)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>{overall?.games || 0}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>m</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="body1" sx={{ color: '#10b981', fontWeight: 700 }}>{overall?.wins || 0}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>W</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="body1" sx={{ color: '#ef4444', fontWeight: 700 }}>{overall?.losses || 0}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>L</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="body1" sx={{ color: getWinrateColor(overall?.winrate || 0), fontWeight: 700 }}>{formatWinrate(overall?.winrate || 0)}%</Typography>
                        </Box>
                        {((overall?.play?.games || 0) + (overall?.draw?.games || 0)) > 0 && (
                            <>
                                <Box sx={{ width: '1px', height: 14, bgcolor: 'rgba(76, 29, 149, 0.4)', mx: 0.5 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PlayArrowIcon sx={{ color: '#10b981', fontSize: 20 }} />
                                    <Typography variant="caption" sx={{ color: 'white' }}>{overall?.play?.games || 0} · {formatWinrate(overall?.play?.winrate || 0)}%</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ShuffleIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                                    <Typography variant="caption" sx={{ color: 'white' }}>{overall?.draw?.games || 0} · {formatWinrate(overall?.draw?.winrate || 0)}%</Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LightbulbIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                        Szybkie wnioski
                    </Typography>
                </Box>
                <Grid container spacing={1.5}>
                    {totalGames > 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <InfoIcon sx={{ color: 'rgba(196, 181, 253, 0.8)', fontSize: 22, mt: 0.25 }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(196, 181, 253, 0.9)' }}>
                                        Na podstawie <strong sx={{ color: 'white' }}>{totalGames}</strong> meczów
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>
                                        {totalGames >= 50 ? 'Wysoka wiarygodność' : totalGames >= 20 ? 'Średnia wiarygodność' : 'Zbieraj więcej danych'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {/* Przewaga Play/Draw */}
                    {playDrawAdvantage && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {playDrawAdvantage.side === 'play' ? (
                                    <PlayArrowIcon sx={{ color: '#10b981', fontSize: 20 }} />
                                ) : (
                                    <ShuffleIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                                )}
                                <Typography variant="body2" sx={{ color: 'white' }}>
                                    Lepiej gdy {playDrawAdvantage.side === 'play' ? 'zaczynasz (Play)' : 'przeciwnik zaczyna (Draw)'}:{' '}
                                    <strong sx={{ color: getWinrateColor(playDrawAdvantage.value) }}>
                                        {formatWinrate(playDrawAdvantage.value)}%
                                    </strong>
                                    {playDrawAdvantage.diff >= 5 && (
                                        <Typography component="span" variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)', ml: 0.5 }}>
                                            (+{formatWinrate(playDrawAdvantage.diff)}% vs drugą opcję)
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>
                        </Grid>
                    )}

                    {/* Forma z ostatnich meczów – zgrupowane w jednym miejscu */}
                    {((last10?.total >= 3) || (last50?.total >= 20) || (last100?.total >= 50)) && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Tooltip title="Im więcej meczów, tym pewniejsza forma – 100+ pokazuje aktualny poziom gry">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                                    <CalendarTodayIcon sx={{ color: '#8b5cf6', fontSize: 20, flexShrink: 0 }} />
                                    <Typography variant="body2" component="span" sx={{ color: 'white' }}>
                                        Forma:{' '}
                                        {last10 && last10.total >= 3 && (
                                            <Box component="span" sx={{ color: getWinrateColor(last10.winrate), fontWeight: 700 }}>10m {formatWinrate(last10.winrate)}%</Box>
                                        )}
                                        {last10?.total >= 3 && (last50?.total >= 20 || last100?.total >= 50) && <Box component="span" sx={{ color: 'rgba(196, 181, 253, 0.4)', mx: 0.25 }}>·</Box>}
                                        {last50 && last50.total >= 20 && (
                                            <Box component="span" sx={{ color: getWinrateColor(last50.winrate), fontWeight: 700 }}>50m {formatWinrate(last50.winrate)}%</Box>
                                        )}
                                        {last50?.total >= 20 && last100?.total >= 50 && <Box component="span" sx={{ color: 'rgba(196, 181, 253, 0.4)', mx: 0.25 }}>·</Box>}
                                        {last100 && last100.total >= 50 && (
                                            <Box component="span" sx={{ color: getWinrateColor(last100.winrate), fontWeight: 700 }}>100m {formatWinrate(last100.winrate)}%</Box>
                                        )}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Grid>
                    )}

                    {/* Osobna linia: najlepszy archetyp, wymaga poprawy, rekomendowana talia */}
                    {(bestArchetype || worstArchetype || bestDeck) && (
                        <Grid item xs={12}>
                            <Divider sx={{ borderColor: 'rgba(76, 29, 149, 0.25)', mb: 1.5 }} />
                        </Grid>
                    )}
                    {bestArchetype && (
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Twój najlepszy archetyp</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <InkColorIcons colors={bestArchetype.colors || bestArchetype.ink_colors || []} size={18} gap={0.5} />
                                <Typography variant="body2" component="span" sx={{ color: 'white', fontWeight: 600 }}>
                                    {bestArchetype.name}{' '}
                                    <Chip label={`${formatWinrate(bestArchetype.winrate)}%`} size="small" sx={{ ml: 0.5, height: 20, fontSize: '0.7rem', bgcolor: getWinrateColor(bestArchetype.winrate), color: 'white' }} />
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>{bestArchetype.games} meczów</Typography>
                        </Grid>
                    )}
                    {worstArchetype && worstArchetype.id !== (bestArchetype && bestArchetype.id) && (
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Wymaga poprawy</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <InkColorIcons colors={worstArchetype.colors || worstArchetype.ink_colors || []} size={18} gap={0.5} />
                                <Typography variant="body2" component="span" sx={{ color: 'white', fontWeight: 600 }}>
                                    {worstArchetype.name}{' '}
                                    <Chip label={`${formatWinrate(worstArchetype.winrate)}%`} size="small" sx={{ ml: 0.5, height: 20, fontSize: '0.7rem', bgcolor: getWinrateColor(worstArchetype.winrate), color: 'white' }} />
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>{worstArchetype.games} meczów</Typography>
                        </Grid>
                    )}
                    {bestDeck && (
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Rekomendowana talia</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <InkColorIcons colors={bestDeck.archetype?.colors || bestDeck.archetype?.ink_colors || []} size={18} gap={0.5} />
                                <Typography variant="body2" component="span" sx={{ color: 'white', fontWeight: 600 }}>
                                    {bestDeck.name}{' '}
                                    <Chip label={`${formatWinrate(bestDeck.winrate)}%`} size="small" sx={{ ml: 0.5, height: 20, fontSize: '0.7rem', bgcolor: getWinrateColor(bestDeck.winrate), color: 'white' }} />
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>{bestDeck.games} meczów</Typography>
                        </Grid>
                    )}

                    {/* Krótka rekomendacja */}
                    {hasEnoughData && (() => {
                        if (bestDeck && worstDeck && worstDeck.winrate < 45 && worstArchetype) {
                            return (
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontStyle: 'italic', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                        W turniejach wybieraj talie z wyższym winrate. Więcej treningu z archetypem{' '}
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <InkColorIcons colors={worstArchetype.colors || worstArchetype.ink_colors || []} size={18} gap={0.35} />
                                            <strong>{worstArchetype.name}</strong>
                                        </Box>
                                        {' '}może podnieść ogólny wynik.
                                    </Typography>
                                </Grid>
                            );
                        }
                        if (bestDeck) {
                            return (
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontStyle: 'italic' }}>
                                        Twoje talie mają zbalansowane wyniki. Sprawdź zakładkę „Matchupy” pod kątem matchupów vs konkretne archetypy przeciwnika.
                                    </Typography>
                                </Grid>
                            );
                        }
                        return null;
                    })()}
                </Grid>
            </CardContent>
        </Card>
    );
}

// Kompaktowy dashboard archetypów – styl sportowy (sortowanie po grach, wiarygodność przy 20+)
function ArchetypesDashboard({ archetypeStats, formatWinrate, getWinrateColor, isSmallMobile, navigate }) {
    const archetypeArray = Object.entries(archetypeStats)
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => (b.games || 0) - (a.games || 0));
    const totalGames = archetypeArray.reduce((s, a) => s + (a.games || 0), 0);
    const withEnoughGames = archetypeArray.filter(a => (a.games || 0) >= MIN_GAMES_RELIABLE);
    const best = withEnoughGames.length > 0 ? withEnoughGames.sort((a, b) => (b.winrate ?? 0) - (a.winrate ?? 0))[0] : null;
    const worst = withEnoughGames.length > 1 ? withEnoughGames.sort((a, b) => (a.winrate ?? 0) - (b.winrate ?? 0))[0] : null;

    const isReliable = (games) => (games || 0) >= MIN_GAMES_RELIABLE;

    if (archetypeArray.length === 0) {
        return (
            <Card sx={{ background: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(76, 29, 149, 0.3)', borderRadius: '16px' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <PaletteIcon sx={{ color: 'rgba(196, 181, 253, 0.4)', fontSize: 48 }} />
                    <Typography variant="h6" sx={{ color: 'white', mt: 2, fontWeight: 600 }}>Brak danych o archetypach</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mt: 0.5 }}>Rozpocznij grę różnymi archetypami</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                    {archetypeArray.length} archetypów · {totalGames} meczów
                </Typography>
                <Tooltip title="Analitycy TCG: mała próbka zawyża/obniża winrate; 20+ min, 100+ = wysoka pewność">
                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', cursor: 'help' }}>
                    Im więcej gier, tym pewniejsze statystyki (20+ min, 100+ zalecane)
                </Typography>
                </Tooltip>
            </Box>

            {(best || worst) && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {best && (
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                '&:hover': { border: '1px solid rgba(16, 185, 129, 0.5)' }
                            }} onClick={() => navigate(`/archetype/${best.id}`)}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>Najlepszy (20+ g)</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <InkColorIcons colors={best.colors || best.ink_colors || []} size={20} gap={0.5} />
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>{best.name}</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: getWinrateColor(best.winrate), fontWeight: 700 }}>{formatWinrate(best.winrate)}% ({best.games} m)</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {worst && worst.id !== best?.id && (
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'rgba(239, 68, 68, 0.08)',
                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                '&:hover': { border: '1px solid rgba(239, 68, 68, 0.45)' }
                            }} onClick={() => navigate(`/archetype/${worst.id}`)}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>Wymaga poprawy (20+ g)</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <InkColorIcons colors={worst.colors || worst.ink_colors || []} size={20} gap={0.5} />
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>{worst.name}</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: getWinrateColor(worst.winrate), fontWeight: 700 }}>{formatWinrate(worst.winrate)}% ({worst.games} m)</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}

            <Card sx={{
                background: 'rgba(17, 24, 39, 0.9)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '12px',
                overflow: 'hidden'
            }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>
                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>Posortowano po liczbie meczów (najczęściej grane)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {archetypeArray.map((archetype, index) => {
                            const reliable = isReliable(archetype.games);
                            return (
                                <Box
                                    key={archetype.id}
                                    onClick={() => navigate(`/archetype/${archetype.id}`)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 2,
                                        cursor: 'pointer',
                                        borderBottom: index < archetypeArray.length - 1 ? '1px solid rgba(76, 29, 149, 0.12)' : 'none',
                                        '&:hover': { background: 'rgba(76, 29, 149, 0.1)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                        <InkColorIcons colors={archetype.colors || archetype.ink_colors || []} size={22} gap={0.5} />
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }} noWrap>{archetype.name}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
                                        {((archetype.play?.games || 0) + (archetype.draw?.games || 0)) > 0 && !isSmallMobile && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 0.5 }}>
                                                <Tooltip title={`Play: ${archetype.play?.games || 0} m · ${formatWinrate(archetype.play?.winrate || 0)}%`}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>P {formatWinrate(archetype.play?.winrate || 0)}%</Typography>
                                                </Tooltip>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.3)' }}>·</Typography>
                                                <Tooltip title={`Draw: ${archetype.draw?.games || 0} m · ${formatWinrate(archetype.draw?.winrate || 0)}%`}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>D {formatWinrate(archetype.draw?.winrate || 0)}%</Typography>
                                                </Tooltip>
                                            </Box>
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: reliable ? getWinrateColor(archetype.winrate) : 'rgba(196, 181, 253, 0.5)',
                                                fontWeight: 600,
                                                minWidth: 52
                                            }}
                                        >
                                            {formatWinrate(archetype.winrate)}% ({archetype.games} m)
                                        </Typography>
                                        {!reliable && (
                                            <Tooltip title="Mała próbka – winrate z kilku gier jest zawodny (17Lands, TCGPlayer: 20+ min, 100+ dla pewności)">
                                                <InfoIcon sx={{ fontSize: 20, color: 'rgba(196, 181, 253, 0.4)' }} />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

// Kompaktowy dashboard talii – sortowanie po grach, wiarygodność przy 20+
function DecksDashboard({ deckStats, formatWinrate, getWinrateColor, isSmallMobile }) {
    const deckArray = Object.entries(deckStats)
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => (b.games || 0) - (a.games || 0));
    const totalGames = deckArray.reduce((s, d) => s + (d.games || 0), 0);
    const withEnoughGames = deckArray.filter(d => (d.games || 0) >= MIN_GAMES_RELIABLE);
    const best = withEnoughGames.length > 0 ? withEnoughGames.sort((a, b) => (b.winrate ?? 0) - (a.winrate ?? 0))[0] : null;
    const worst = withEnoughGames.length > 1 ? withEnoughGames.sort((a, b) => (a.winrate ?? 0) - (b.winrate ?? 0))[0] : null;

    const isReliable = (games) => (games || 0) >= MIN_GAMES_RELIABLE;

    if (deckArray.length === 0) {
        return (
            <Card sx={{ background: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(76, 29, 149, 0.3)', borderRadius: '16px' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <DeckIcon sx={{ color: 'rgba(196, 181, 253, 0.4)', fontSize: 48 }} />
                    <Typography variant="h6" sx={{ color: 'white', mt: 2, fontWeight: 600 }}>Brak danych o taliach</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mt: 0.5 }}>Użyj talii w meczach</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                    {deckArray.length} talii · {totalGames} meczów
                </Typography>
                <Tooltip title="Analitycy TCG: mała próbka zawyża/obniża winrate; 20+ min, 100+ = wysoka pewność">
                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', cursor: 'help' }}>
                    Im więcej gier, tym pewniejsze statystyki (20+ min, 100+ zalecane)
                </Typography>
                </Tooltip>
            </Box>

            {(best || worst) && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {best && (
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                borderRadius: '12px'
                            }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>Rekomendowana (20+ g)</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <InkColorIcons colors={best.archetype?.colors || best.archetype?.ink_colors || []} size={20} gap={0.5} />
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>{best.name}</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: getWinrateColor(best.winrate), fontWeight: 700 }}>{formatWinrate(best.winrate)}% ({best.games} m)</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {worst && worst.id !== best?.id && (
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'rgba(239, 68, 68, 0.08)',
                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                borderRadius: '12px'
                            }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>Najsłabsza (20+ g)</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <InkColorIcons colors={worst.archetype?.colors || worst.archetype?.ink_colors || []} size={20} gap={0.5} />
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>{worst.name}</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: getWinrateColor(worst.winrate), fontWeight: 700 }}>{formatWinrate(worst.winrate)}% ({worst.games} m)</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}

            <Card sx={{
                background: 'rgba(17, 24, 39, 0.9)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '12px',
                overflow: 'hidden'
            }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>
                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>Posortowano po liczbie meczów (najczęściej grane)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {deckArray.map((deck, index) => {
                            const reliable = isReliable(deck.games);
                            return (
                                <Box
                                    key={deck.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 2,
                                        borderBottom: index < deckArray.length - 1 ? '1px solid rgba(76, 29, 149, 0.12)' : 'none',
                                        '&:hover': { background: 'rgba(76, 29, 149, 0.08)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                        <InkColorIcons colors={deck.archetype?.colors || deck.archetype?.ink_colors || []} size={22} gap={0.5} />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }} noWrap>{deck.name}</Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>{deck.archetype?.name || '—'}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
                                        {((deck.play?.games || 0) + (deck.draw?.games || 0)) > 0 && !isSmallMobile && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 0.5 }}>
                                                <Tooltip title={`Play: ${deck.play?.games || 0} m · ${formatWinrate(deck.play?.winrate || 0)}%`}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>P {formatWinrate(deck.play?.winrate || 0)}%</Typography>
                                                </Tooltip>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.3)' }}>·</Typography>
                                                <Tooltip title={`Draw: ${deck.draw?.games || 0} m · ${formatWinrate(deck.draw?.winrate || 0)}%`}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>D {formatWinrate(deck.draw?.winrate || 0)}%</Typography>
                                                </Tooltip>
                                            </Box>
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: reliable ? getWinrateColor(deck.winrate) : 'rgba(196, 181, 253, 0.5)',
                                                fontWeight: 600,
                                                minWidth: 52
                                            }}
                                        >
                                            {formatWinrate(deck.winrate)}% ({deck.games} m)
                                        </Typography>
                                        {!reliable && (
                                            <Tooltip title="Mała próbka – winrate z kilku gier jest zawodny (17Lands, TCGPlayer: 20+ min, 100+ dla pewności)">
                                                <InfoIcon sx={{ fontSize: 20, color: 'rgba(196, 181, 253, 0.4)' }} />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

// Matchupy – Moja talia vs archetyp przeciwnika (17Lands/TCGPlayer style)
const MIN_MATCHUPS_RELIABLE = 5;
const getArchetypeColors = (a) => a?.ink_colors || a?.colors || [];

function MatchupsDashboard({ matches = [], allArchetypes = [], formatWinrate, getWinrateColor, isSmallMobile, navigate }) {
    const [filterDeck, setFilterDeck] = useState('all');
    const [filterArchetype, setFilterArchetype] = useState('all');

    const archetypeMap = React.useMemo(() => {
        const m = {};
        (allArchetypes || []).forEach(a => { m[a.id] = a; });
        return m;
    }, [allArchetypes]);

    const { uniqueDecks, uniqueArchetypes } = React.useMemo(() => {
        const decks = new Map();
        const archs = new Map();
        (matches || []).forEach(m => {
            if (m.myDeckInfo) {
                decks.set(m.myDeckId, { id: m.myDeckId, name: m.myDeckInfo.name || 'Talia' });
                const archRaw = m.myDeckInfo.archetype;
                const arch = typeof archRaw === 'object' && archRaw?.id ? archRaw : archetypeMap[archRaw];
                if (arch) archs.set(arch.id, arch);
            }
        });
        return { uniqueDecks: Array.from(decks.values()), uniqueArchetypes: Array.from(archs.values()) };
    }, [matches, archetypeMap]);

    const filteredMatches = React.useMemo(() => {
        let out = matches || [];
        if (filterDeck !== 'all') out = out.filter(m => m.myDeckId === filterDeck);
        if (filterArchetype !== 'all') {
            out = out.filter(m => {
                const archRaw = m.myDeckInfo?.archetype;
                const archId = typeof archRaw === 'object' && archRaw?.id ? archRaw.id : archRaw;
                return archId === filterArchetype;
            });
        }
        return out;
    }, [matches, filterDeck, filterArchetype]);

    const matchups = calculateArchetypeMatchups(filteredMatches, archetypeMap);
    const arr = Object.values(matchups).sort((a, b) => b.games - a.games);
    const favorable = arr.filter(m => m.games >= MIN_MATCHUPS_RELIABLE && m.winrate >= 55).slice(0, 5);
    const unfavorable = arr.filter(m => m.games >= MIN_MATCHUPS_RELIABLE && m.winrate < 45).sort((a, b) => a.winrate - b.winrate).slice(0, 5);

    if ((matches || []).length === 0) {
        return (
            <Card sx={{ background: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(76, 29, 149, 0.3)', borderRadius: '12px' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <SwapHorizIcon sx={{ color: 'rgba(196, 181, 253, 0.4)', fontSize: 48 }} />
                    <Typography variant="h6" sx={{ color: 'white', mt: 2, fontWeight: 600 }}>Brak danych o matchupach</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mt: 0.5 }}>Rozgrywaj mecze różnymi taliami przeciw różnym archetypom przeciwnika</Typography>
                </CardContent>
            </Card>
        );
    }

    const renderCompactRow = (m, index) => {
        const unreliable = m.games < MIN_MATCHUPS_RELIABLE;
        return (
            <Box
                key={`${m.myArchetype.id}-vs-${m.opponentArchetype.id}`}
                onClick={() => navigate(`/archetype/${m.myArchetype.id}`)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 2, px: 1.5, py: 0.75,
                    cursor: 'pointer', borderBottom: '1px solid rgba(76, 29, 149, 0.08)',
                    background: index % 2 === 1 ? 'rgba(76, 29, 149, 0.04)' : 'transparent',
                    opacity: unreliable ? 0.5 : 1,
                    '&:hover': { background: 'rgba(76, 29, 149, 0.12)' }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '0 1 auto' }}>
                    <InkColorIcons colors={getArchetypeColors(m.myArchetype)} size={20} gap={0.35} />
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, minWidth: 0 }} noWrap>{m.myArchetype.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.4)', flexShrink: 0 }}>vs</Typography>
                    <InkColorIcons colors={getArchetypeColors(m.opponentArchetype)} size={20} gap={0.35} />
                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.85)', minWidth: 0 }} noWrap>{m.opponentArchetype.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Chip size="small" label={`${formatWinrate(m.winrate)}%`} sx={{ height: 18, fontSize: '0.65rem', bgcolor: unreliable ? '#6b7280' : getWinrateColor(m.winrate), color: 'white', fontWeight: 600 }} />
                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: '0.7rem', minWidth: 28 }}>{m.games}g</Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box>
            {/* Filtry */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterListIcon sx={{ color: 'rgba(196, 181, 253, 0.4)', fontSize: 22 }} />
                    <FormControl size="small" sx={{ minWidth: isSmallMobile ? '100%' : 140 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Talia</InputLabel>
                        <Select
                            value={filterDeck}
                            onChange={(e) => setFilterDeck(e.target.value)}
                            label="Talia"
                            sx={{ color: 'white', fontSize: '0.8125rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.4)' } }}
                        >
                            <MenuItem value="all">Wszystkie talie</MenuItem>
                            {uniqueDecks.map(d => (
                                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <FormControl size="small" sx={{ minWidth: isSmallMobile ? '100%' : 160 }}>
                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Mój archetyp</InputLabel>
                    <Select
                        value={filterArchetype}
                        onChange={(e) => setFilterArchetype(e.target.value)}
                        label="Mój archetyp"
                        sx={{ color: 'white', fontSize: '0.8125rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.4)' } }}
                    >
                        <MenuItem value="all">Wszystkie archetypy</MenuItem>
                        {uniqueArchetypes.map(a => (
                            <MenuItem key={a.id} value={a.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <InkColorIcons colors={getArchetypeColors(a)} size={18} gap={0.3} />
                                    {a.name}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>{arr.length} matchupów</Typography>
                <Tooltip title="5+ g = wiarygodne · 20+ = pewniejsza statystyka">
                    <InfoIcon sx={{ fontSize: 20, color: 'rgba(196, 181, 253, 0.4)', cursor: 'help' }} />
                </Tooltip>
            </Box>

            {/* Favorable / Unfavorable – kompaktowe chipy */}
            {(favorable.length > 0 || unfavorable.length > 0) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {favorable.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(16, 185, 129, 0.9)', fontWeight: 600 }}>✓&nbsp;</Typography>
                            {favorable.map(m => (
                                <Chip
                                    key={`f-${m.myArchetype.id}-${m.opponentArchetype.id}`}
                                    size="small"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <InkColorIcons colors={getArchetypeColors(m.myArchetype)} size={18} gap={0.3} />
                                            <span>{m.myArchetype.name}</span>
                                            <span style={{ opacity: 0.7 }}>vs</span>
                                            <InkColorIcons colors={getArchetypeColors(m.opponentArchetype)} size={18} gap={0.3} />
                                            <span>{m.opponentArchetype.name}</span>
                                            <span style={{ fontWeight: 700 }}>{formatWinrate(m.winrate)}%</span>
                                        </Box>
                                    }
                                    onClick={() => navigate(`/archetype/${m.myArchetype.id}`)}
                                    sx={{ height: 24, fontSize: '0.7rem', bgcolor: 'rgba(16, 185, 129, 0.2)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.4)', cursor: 'pointer' }}
                                />
                            ))}
                        </Box>
                    )}
                    {unfavorable.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(239, 68, 68, 0.9)', fontWeight: 600 }}>✗&nbsp;</Typography>
                            {unfavorable.map(m => (
                                <Chip
                                    key={`u-${m.myArchetype.id}-${m.opponentArchetype.id}`}
                                    size="small"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <InkColorIcons colors={getArchetypeColors(m.myArchetype)} size={18} gap={0.3} />
                                            <span>{m.myArchetype.name}</span>
                                            <span style={{ opacity: 0.7 }}>vs</span>
                                            <InkColorIcons colors={getArchetypeColors(m.opponentArchetype)} size={18} gap={0.3} />
                                            <span>{m.opponentArchetype.name}</span>
                                            <span style={{ fontWeight: 700 }}>{formatWinrate(m.winrate)}%</span>
                                        </Box>
                                    }
                                    onClick={() => navigate(`/archetype/${m.myArchetype.id}`)}
                                    sx={{ height: 24, fontSize: '0.7rem', bgcolor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', cursor: 'pointer' }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Główna tabela – kompaktowa */}
            <Card sx={{ background: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(76, 29, 149, 0.3)', borderRadius: '12px', overflow: 'hidden' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)', fontWeight: 600 }}>Moja talia vs Przeciwnik · sort. po grach</Typography>
                    </Box>
                    <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>{arr.map((m, i) => renderCompactRow(m, i))}</Box>
                </CardContent>
            </Card>
        </Box>
    );
}

// Trendy – rolling winrate, forma (TCG analyst style)
const ROLLING_WINDOWS = [10, 20, 50];

function TrendsDashboard({ matches = [], allArchetypes = [], formatWinrate, getWinrateColor, isSmallMobile }) {
    const [filterDeck, setFilterDeck] = useState('all');
    const [filterArchetype, setFilterArchetype] = useState('all');
    const [windowSize, setWindowSize] = useState(20);

    const archetypeMap = React.useMemo(() => {
        const m = {};
        (allArchetypes || []).forEach(a => { m[a.id] = a; });
        return m;
    }, [allArchetypes]);

    const { uniqueDecks, uniqueArchetypes } = React.useMemo(() => {
        const decks = new Map();
        const archs = new Map();
        (matches || []).forEach(m => {
            if (m.myDeckInfo) {
                decks.set(m.myDeckId, { id: m.myDeckId, name: m.myDeckInfo.name || 'Talia' });
                const archRaw = m.myDeckInfo.archetype;
                const arch = typeof archRaw === 'object' && archRaw?.id ? archRaw : archetypeMap[archRaw];
                if (arch) archs.set(arch.id, arch);
            }
        });
        return { uniqueDecks: Array.from(decks.values()), uniqueArchetypes: Array.from(archs.values()) };
    }, [matches, archetypeMap]);

    const filteredMatches = React.useMemo(() => {
        let out = matches || [];
        if (filterDeck !== 'all') out = out.filter(m => m.myDeckId === filterDeck);
        if (filterArchetype !== 'all') {
            out = out.filter(m => {
                const archRaw = m.myDeckInfo?.archetype;
                const archId = typeof archRaw === 'object' && archRaw?.id ? archRaw.id : archRaw;
                return archId === filterArchetype;
            });
        }
        return out;
    }, [matches, filterDeck, filterArchetype]);

    const rolling = calculateRollingWinrate(filteredMatches, windowSize);
    const currentWR = rolling.length > 0 ? rolling[rolling.length - 1].winrate : null;
    const trend = rolling.length >= 5
        ? (() => {
            const last3 = rolling.slice(-3).reduce((s, r) => s + r.winrate, 0) / 3;
            const prev3 = rolling.slice(-6, -3).length >= 3 ? rolling.slice(-6, -3).reduce((s, r) => s + r.winrate, 0) / 3 : last3;
            return last3 > prev3 ? 'up' : last3 < prev3 ? 'down' : null;
        })()
        : null;
    const minWR = rolling.length > 0 ? Math.min(...rolling.map(r => r.winrate)) : 0;
    const maxWR = rolling.length > 0 ? Math.max(...rolling.map(r => r.winrate)) : 100;

    if (!matches?.length) {
        return (
            <Card sx={{ background: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(76, 29, 149, 0.3)', borderRadius: '12px' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <ShowChartIcon sx={{ color: 'rgba(196, 181, 253, 0.4)', fontSize: 48 }} />
                    <Typography variant="h6" sx={{ color: 'white', mt: 2, fontWeight: 600 }}>Brak danych do trendów</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mt: 0.5 }}>Rozgrywaj mecze – zobaczysz jak zmienia się Twoja forma (rolling winrate)</Typography>
                </CardContent>
            </Card>
        );
    }

    const chartHeight = 200;
    const chartPadding = { top: 8, right: 8, bottom: 24, left: 36 };

    return (
        <Box>
            {/* Filtry + okno */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterListIcon sx={{ color: 'rgba(196, 181, 253, 0.4)', fontSize: 22 }} />
                    <FormControl size="small" sx={{ minWidth: isSmallMobile ? '100%' : 130 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Talia</InputLabel>
                        <Select value={filterDeck} onChange={(e) => setFilterDeck(e.target.value)} label="Talia" sx={{ color: 'white', fontSize: '0.8125rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.4)' } }}>
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {uniqueDecks.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                <FormControl size="small" sx={{ minWidth: isSmallMobile ? '100%' : 150 }}>
                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Archetyp</InputLabel>
                    <Select value={filterArchetype} onChange={(e) => setFilterArchetype(e.target.value)} label="Archetyp" sx={{ color: 'white', fontSize: '0.8125rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.4)' } }}>
                        <MenuItem value="all">Wszystkie</MenuItem>
                        {uniqueArchetypes.map(a => <MenuItem key={a.id} value={a.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><InkColorIcons colors={getArchetypeColors(a)} size={18} gap={0.3} />{a.name}</Box></MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Okno</InputLabel>
                    <Select value={windowSize} onChange={(e) => setWindowSize(Number(e.target.value))} label="Okno" sx={{ color: 'white', fontSize: '0.8125rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.4)' } }}>
                        {ROLLING_WINDOWS.map(w => <MenuItem key={w} value={w}>{w} gier</MenuItem>)}
                    </Select>
                </FormControl>
                <Tooltip title="Rolling winrate = średnia z ostatnich N gier (np. 20 = każdy punkt to winrate z 20 poprzedzających gier)">
                    <InfoIcon sx={{ fontSize: 20, color: 'rgba(196, 181, 253, 0.4)', cursor: 'help' }} />
                </Tooltip>
            </Box>

            {/* Brak danych po filtrach */}
            {filteredMatches.length < windowSize && (
                <Card sx={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px', mb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.9)' }}>
                            Po filtrach: {filteredMatches.length} gier · potrzeba min. {windowSize} dla rolling winrate
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', display: 'block', mt: 0.5 }}>Zmień filtry lub okno (np. 10 gier)</Typography>
                    </CardContent>
                </Card>
            )}

            {/* Aktualna forma + KPI */}
            {filteredMatches.length >= windowSize && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" sx={{ color: currentWR != null ? getWinrateColor(currentWR) : 'white', fontWeight: 700 }}>{currentWR != null ? `${formatWinrate(currentWR)}%` : '—'}</Typography>
                            {trend === 'up' && <TrendingUpIcon sx={{ color: '#10b981', fontSize: 22 }} />}
                            {trend === 'down' && <TrendingDownIcon sx={{ color: '#ef4444', fontSize: 22 }} />}
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>aktualna forma (rolling {windowSize})</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>min {formatWinrate(minWR)}%</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>max {formatWinrate(maxWR)}%</Typography>
                        </Box>
                    </Box>

                    {/* Wykres linii rolling winrate */}
                    <Card sx={{ background: 'rgba(17, 24, 39, 0.95)', border: '1px solid rgba(76, 29, 149, 0.4)', borderRadius: '12px', mb: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.9)', fontWeight: 600 }}>Rolling winrate (okno {windowSize} gier) · od lewej = starsze mecze</Typography>
                            <Box sx={{ position: 'relative', height: chartHeight + chartPadding.top + chartPadding.bottom, mt: 1.5 }}>
                                {/* Siatka tła */}
                                <Box sx={{ position: 'absolute', left: chartPadding.left, right: chartPadding.right, top: chartPadding.top, height: chartHeight, display: 'flex', flexDirection: 'column' }}>
                                    {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
                                        <Box key={i} sx={{ flex: 1, borderBottom: i < 4 ? '1px solid rgba(76, 29, 149, 0.15)' : 'none' }} />
                                    ))}
                                </Box>
                                {/* Skala Y */}
                                <Box sx={{ position: 'absolute', left: 0, top: chartPadding.top, bottom: chartPadding.bottom, width: chartPadding.left - 4, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: 'rgba(196, 181, 253, 0.5)', fontSize: '0.7rem', fontWeight: 500 }}>
                                    <span>100%</span>
                                    <span>75%</span>
                                    <span>50%</span>
                                    <span>25%</span>
                                    <span>0%</span>
                                </Box>
                                {/* Wykres SVG */}
                                <Box sx={{ position: 'absolute', left: chartPadding.left, right: chartPadding.right, top: chartPadding.top, height: chartHeight }}>
                                    <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
                                        <defs>
                                            <linearGradient id="trendAreaGrad" x1="0" y1="1" x2="0" y2="0">
                                                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.15} />
                                                <stop offset="0.5" stopColor="#f59e0b" stopOpacity={0.08} />
                                                <stop offset="100%" stopColor="#10b981" stopOpacity={0.15} />
                                            </linearGradient>
                                            <linearGradient id="trendLineGrad" x1="0" y1="100" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                                                <stop offset="0%" stopColor="#ef4444" />
                                                <stop offset="0.5" stopColor="#f59e0b" />
                                                <stop offset="100%" stopColor="#10b981" />
                                            </linearGradient>
                                        </defs>
                                        {rolling.length > 0 && (() => {
                                            const n = rolling.length;
                                            const pts = n === 1
                                                ? [{ x: 0, y: 100 - (rolling[0].winrate / 100) * 100 }, { x: 100, y: 100 - (rolling[0].winrate / 100) * 100 }]
                                                : rolling.map((r, i) => ({ x: (i / Math.max(1, n - 1)) * 100, y: 100 - (r.winrate / 100) * 100 }));
                                            const tension = 0.35;
                                            const smoothPath = pts.length <= 2 ? `M ${pts[0].x} ${pts[0].y} L ${pts[pts.length - 1].x} ${pts[pts.length - 1].y}` : pts.reduce((acc, p, i) => {
                                                if (i === 0) return `M ${p.x} ${p.y}`;
                                                const prev = pts[i - 1];
                                                const next = pts[i + 1];
                                                const pPrev = pts[i - 2] || prev;
                                                const cp1x = prev.x + (p.x - pPrev.x) * tension;
                                                const cp1y = prev.y + (p.y - pPrev.y) * tension;
                                                const cp2x = p.x - (next ? (next.x - prev.x) * tension : 0);
                                                const cp2y = p.y - (next ? (next.y - prev.y) * tension : 0);
                                                return `${acc} C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(2)} ${p.x} ${p.y}`;
                                            }, '');
                                            const curvePart = smoothPath.indexOf('C') >= 0 ? smoothPath.substring(smoothPath.indexOf('C')) : '';
                                            const areaPathD = `M 0 100 L ${pts[0].x} ${pts[0].y} ${curvePart} L 100 100 Z`;
                                            return (
                                                <>
                                                    <path fill="url(#trendAreaGrad)" d={areaPathD} />
                                                    <path
                                                        fill="none"
                                                        stroke="url(#trendLineGrad)"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        vectorEffect="non-scaling-stroke"
                                                        d={smoothPath}
                                                    />
                                                </>
                                            );
                                        })()}
                                    </svg>
                                </Box>
                                {/* Overlay do tooltipów */}
                                <Box sx={{ position: 'absolute', left: chartPadding.left, right: chartPadding.right, top: chartPadding.top, height: chartHeight, display: 'flex' }}>
                                    {rolling.map((r, i) => (
                                        <Tooltip key={i} title={`Gra #${r.i} · ${formatWinrate(r.winrate)}%`} placement="top" arrow>
                                            <Box sx={{ flex: 1, height: '100%', cursor: 'crosshair' }} />
                                        </Tooltip>
                                    ))}
                                </Box>
                                {/* Dolna oś */}
                                <Box sx={{ position: 'absolute', left: chartPadding.left, right: chartPadding.right, bottom: 0, display: 'flex', justifyContent: 'space-between', color: 'rgba(196, 181, 253, 0.4)', fontSize: '0.65rem' }}>
                                    <span>#{rolling.length > 0 ? rolling[0].i : ''}</span>
                                    <span>#{rolling.length > 0 ? rolling[rolling.length - 1].i : ''}</span>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
}

// Komponent znajomości archetypów – znajomość = gry ROZEGRANE danym archetypem (perArchetype)
// Sortowanie po popularności w mecie (gry vs) – popularniejsze decki na górze
function ArchetypeKnowledgePanel({ perArchetypeStats = {}, matches = [], allArchetypes = [], matrixVsCount = null, loading = false }) {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [sortBy, setSortBy] = useState('global');

    // Popularność: globalna = z matrixa (mecze wszystkich użytkowników), twoja = tylko twoje mecze
    const userVsCount = {};
    (matches || []).forEach(m => {
        const oid = m.opponentArchetypeId;
        if (!oid) return;
        userVsCount[oid] = (userVsCount[oid] || 0) + 1;
    });
    const hasGlobal = matrixVsCount && Object.keys(matrixVsCount).length > 0;
    const hasMeta = Object.keys(userVsCount).length > 0;
    React.useEffect(() => {
        if ((sortBy === 'global' && !hasGlobal) || (sortBy === 'meta' && !hasMeta)) {
            setSortBy(hasGlobal ? 'global' : 'meta');
        }
    }, [hasGlobal, hasMeta, sortBy]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <Typography variant="h6" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Ładowanie danych...
                </Typography>
            </Box>
        );
    }

    const perArchetype = perArchetypeStats || {};

    const effectiveSort = (sortBy === 'global' && hasGlobal) ? 'global'
        : (sortBy === 'meta' && hasMeta) ? 'meta'
        : (hasGlobal ? 'global' : hasMeta ? 'meta' : 'mecze');
    const popularitySource = effectiveSort === 'global' ? matrixVsCount : userVsCount;
    const maxPopularity = Math.max(1, ...Object.values(popularitySource));

    // Wymagane gry rosną z popularnością – im popularniejszy deck, tym więcej gier żeby go ogarnąć
    const getExpectedGames = (popularity) => {
        if (!popularity || popularity === 0) return 5;
        const ratio = popularity / maxPopularity;
        return Math.round(5 + ratio * 45); // 5–50 gier, zależnie od popularności
    };

    const getExperienceLevel = (games) => {
        if (games === 0) return { level: 'Brak gier', color: '#6b7280', range: '0' };
        if (games < 10) return { level: 'Nowicjusz', color: '#6b7280', range: '1-9' };
        if (games < 30) return { level: 'Początkujący', color: '#3b82f6', range: '10-29' };
        if (games < 60) return { level: 'Średni', color: '#10b981', range: '30-59' };
        if (games < 100) return { level: 'Zaawansowany', color: '#f59e0b', range: '60-99' };
        if (games < 200) return { level: 'Ekspert', color: '#ef4444', range: '100-199' };
        return { level: 'Mistrz', color: '#8b5cf6', range: '200+' };
    };

    // Wszystkie archetypy – ZNAJOMOŚĆ (gry z), sortowanie po popularności (globalna lub user vs)
    const sortedArchetypes = (allArchetypes || []).map(arch => {
        const stats = perArchetype[arch.id] || { games: 0, wins: 0, losses: 0, winrate: 0, play: { games: 0, winrate: 0 }, draw: { games: 0, winrate: 0 } };
        const popularity = popularitySource[arch.id] || 0;
        const expectedGames = getExpectedGames(popularity);
        const isBelowExpected = popularity > 0 && (stats.games || 0) < expectedGames;
        return {
            id: arch.id,
            name: arch.name || stats.name || 'Nieznany',
            colors: arch.ink_colors || arch.colors || stats.colors || [],
            games: stats.games || 0,
            wins: stats.wins || 0,
            losses: stats.losses || 0,
            winrate: stats.winrate ?? 0,
            play: stats.play || { games: 0, winrate: 0 },
            draw: stats.draw || { games: 0, winrate: 0 },
            experience: getExperienceLevel(stats.games || 0),
            popularity,
            expectedGames,
            isBelowExpected
        };
    }).sort((a, b) => b.popularity - a.popularity);

    const totalGames = sortedArchetypes.reduce((s, a) => s + a.games, 0);
    // Popularne decki poniżej oczekiwanego progu – warto pograć
    const toPlay = sortedArchetypes.filter(a => a.isBelowExpected).slice(0, 5);

    return (
        <Box>
            {/* Po co ta zakładka */}
            <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', background: 'rgba(76, 29, 149, 0.12)', border: '1px solid rgba(76, 29, 149, 0.2)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.9)' }}>
                    <strong>Po co to?</strong> Żeby lepiej ogarniać metę, warto znać popularne decki od środka – grając nimi rozumiesz ich słabości. Im popularniejszy deck, tym więcej gier nim powinieneś rozegrać. Lista sortowana po popularności: <strong>globalna meta</strong> – na podstawie meczów wszystkich użytkowników, lub <strong>twoja meta</strong> – tylko na podstawie twoich meczy.
                </Typography>
                {(hasGlobal || hasMeta) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>Sortowanie:</Typography>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                                value={hasGlobal && hasMeta ? sortBy : (hasGlobal ? 'global' : 'meta')}
                                onChange={(e) => setSortBy(e.target.value)}
                                sx={{
                                    color: 'rgba(196, 181, 253, 0.95)',
                                    fontSize: '0.75rem',
                                    height: 28,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.4)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124, 58, 237, 0.6)' }
                                }}
                            >
                                <MenuItem value="global" disabled={!hasGlobal}>Globalna meta</MenuItem>
                                <MenuItem value="meta" disabled={!hasMeta}>Twoja meta</MenuItem>
                            </Select>
                        </FormControl>
                        {effectiveSort === 'global' && <Typography variant="caption" sx={{ color: 'rgba(16, 185, 129, 0.8)' }}>mecze wszystkich użytkowników</Typography>}
                        {effectiveSort === 'meta' && <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>tylko twoje mecze</Typography>}
                    </Box>
                )}
            </Box>

            {/* Rekomendacja: zagraj tymi taliami */}
            {toPlay.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                    <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>Zagraj tymi deckami, żeby lepiej ogarniać metę:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {toPlay.map(a => (
                            <Box
                                key={a.id}
                                onClick={() => navigate(`/archetype/${a.id}`)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: '6px',
                                    background: 'rgba(17, 24, 39, 0.6)',
                                    cursor: 'pointer',
                                    '&:hover': { background: 'rgba(16, 185, 129, 0.2)' }
                                }}
                            >
                                <InkColorIcons colors={a.colors || []} size={16} gap={0.25} />
                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>{a.name}</Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>({a.games} g)</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Nagłówek + legenda */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                    {sortedArchetypes.length} archetypów · {totalGames} gier
                </Typography>
                <Tooltip title={effectiveSort === 'global' ? "Sortowanie: mecze wszystkich użytkowników" : "Sortowanie: tylko twoje mecze"}>
                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', cursor: 'help' }}>
                        Sortowanie: {effectiveSort === 'global' ? 'globalna meta' : 'twoja meta'}
                    </Typography>
                </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                {[
                    { level: 'Brak', range: '0', color: '#6b7280' },
                    { level: 'Nowicjusz', range: '1-9', color: '#6b7280' },
                    { level: 'Początkujący', range: '10-29', color: '#3b82f6' },
                    { level: 'Średni', range: '30-59', color: '#10b981' },
                    { level: 'Zaawansowany', range: '60-99', color: '#f59e0b' },
                    { level: 'Ekspert', range: '100-199', color: '#ef4444' },
                    { level: 'Mistrz', range: '200+', color: '#8b5cf6' }
                ].map(item => (
                    <Tooltip key={item.level} title={`${item.level}: ${item.range} gier`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'help' }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: item.color }} />
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.65rem' }}>{item.level}</Typography>
                        </Box>
                    </Tooltip>
                ))}
            </Box>

            {/* Lista – tylko liczba gier, bez winrate */}
            {sortedArchetypes.length > 0 ? (
                <Card key={`archetype-list-${effectiveSort}`} sx={{
                    background: 'rgba(17, 24, 39, 0.9)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    <CardContent sx={{ p: 0 }}>
                        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)', fontWeight: 600 }}>Znajomość (gry danym archetypem)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {sortedArchetypes.map((archetype, index) => {
                                const hasExpected = archetype.popularity > 0 && archetype.expectedGames > 0;
                                const progress = hasExpected ? Math.min(100, (archetype.games / archetype.expectedGames) * 100) : 0;
                                const row = (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 1.5,
                                            py: 1,
                                            cursor: 'pointer',
                                            borderLeft: archetype.isBelowExpected ? '3px solid #f59e0b' : '3px solid transparent',
                                            borderBottom: index < sortedArchetypes.length - 1 ? '1px solid rgba(76, 29, 149, 0.1)' : 'none',
                                            background: archetype.isBelowExpected ? 'rgba(245, 158, 11, 0.06)' : 'transparent',
                                            '&:hover': { background: archetype.isBelowExpected ? 'rgba(245, 158, 11, 0.12)' : 'rgba(76, 29, 149, 0.08)' }
                                        }}
                                    >
                                        <InkColorIcons colors={archetype.colors || []} size={18} gap={0.4} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }} noWrap>{archetype.name}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: archetype.experience.color }} />
                                                    <Typography variant="caption" sx={{ color: archetype.experience.color, fontWeight: 600, fontSize: '0.7rem' }}>{archetype.experience.level}</Typography>
                                                </Box>
                                            </Box>
                                            {hasExpected && (
                                                <Tooltip title={`${archetype.games} z ${archetype.expectedGames} oczekiwanych (popularny deck)`}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                        <Box sx={{ flex: 1, maxWidth: 80, height: 4, borderRadius: 2, bgcolor: 'rgba(76, 29, 149, 0.3)', overflow: 'hidden' }}>
                                                            <Box sx={{ width: `${progress}%`, height: '100%', bgcolor: progress >= 100 ? '#10b981' : archetype.isBelowExpected ? '#f59e0b' : 'rgba(196, 181, 253, 0.6)', transition: 'width 0.2s' }} />
                                                        </Box>
                                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)', fontSize: '0.7rem', minWidth: 36 }}>
                                                            {archetype.games}/{archetype.expectedGames}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        {!hasExpected && (
                                            <Typography variant="body2" sx={{ color: archetype.games > 0 ? 'rgba(196, 181, 253, 0.9)' : 'rgba(196, 181, 253, 0.5)', fontWeight: 600, fontSize: '0.8125rem', minWidth: 40 }}>
                                                {archetype.games} g
                                            </Typography>
                                        )}
                                    </Box>
                                );
                                return archetype.isBelowExpected ? (
                                    <Tooltip key={archetype.id} title="Popularny deck – zagraj więcej, żeby lepiej go znać" enterDelay={400}>{row}</Tooltip>
                                ) : (
                                    <React.Fragment key={archetype.id}>{row}</React.Fragment>
                                );
                            })}
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{ background: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(76, 29, 149, 0.3)', borderRadius: '16px' }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <PsychologyIcon sx={{ color: 'rgba(196, 181, 253, 0.4)', fontSize: 48 }} />
                        <Typography variant="h6" sx={{ color: 'white', mt: 2, fontWeight: 600 }}>Brak danych</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mt: 0.5 }}>Rozgrywaj mecze różnymi taliami – zobaczysz znajomość każdego archetypu</Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}

function UserStatsPanel({ userData, loading = false, trackerFilters }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [activeTab, setActiveTab] = useState(0);
    const [matches, setMatches] = useState([]);
    const [allArchetypesForMatches, setAllArchetypesForMatches] = useState([]);
    const [matrixVsCount, setMatrixVsCount] = useState(null);
    const [matchesLoading, setMatchesLoading] = useState(false);

    useEffect(() => {
        if (!currentUser?.uid) return;
        let cancelled = false;
        setMatchesLoading(true);
        Promise.all([
            getUserMatchesWithDetails(currentUser.uid, trackerFilters),
            getAllArchetypes(trackerFilters),
            getAggregatedArchetypeStats(currentUser.uid, trackerFilters)
        ])
            .then(([matchesData, scopedArchetypes, aggregatedStats]) => {
                if (!cancelled) {
                    setMatches(matchesData || []);
                    setAllArchetypesForMatches(scopedArchetypes || []);
                    // Z matrix: suma gier vs każdy archetyp (kolumny)
                    const vsCount = {};
                    Object.values(aggregatedStats || {}).forEach(arch => {
                        Object.entries(arch.vsArchetypes || {}).forEach(([oppId, vs]) => {
                            vsCount[oppId] = (vsCount[oppId] || 0) + (vs.games || 0);
                        });
                    });
                    setMatrixVsCount(Object.keys(vsCount).length > 0 ? vsCount : null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMatches([]);
                    setAllArchetypesForMatches([]);
                    setMatrixVsCount(null);
                }
            })
            .finally(() => { if (!cancelled) setMatchesLoading(false); });
        return () => { cancelled = true; };
    }, [currentUser?.uid, trackerFilters]);

    const stats = useMemo(() => {
        if (!matches || matches.length === 0) return null;

        const resultCounters = {
            wins: 0,
            losses: 0,
            draws: 0
        };
        const playCounters = { wins: 0, losses: 0, draws: 0, games: 0 };
        const drawCounters = { wins: 0, losses: 0, draws: 0, games: 0 };
        const perDeck = {};
        const perArchetype = {};

        const toWinrate = (wins, losses) => (wins + losses > 0 ? (wins / (wins + losses)) * 100 : 0);
        const ensureBucket = (store, key, seed) => {
            if (!store[key]) {
                store[key] = {
                    ...seed,
                    games: 0,
                    wins: 0,
                    losses: 0,
                    draws: 0,
                    winrate: 0,
                    play: { games: 0, wins: 0, losses: 0, draws: 0, winrate: 0 },
                    draw: { games: 0, wins: 0, losses: 0, draws: 0, winrate: 0 }
                };
            }
            return store[key];
        };
        const applyResult = (bucket, result, axis) => {
            bucket.games += 1;
            if (result === 'win') bucket.wins += 1;
            else if (result === 'lose') bucket.losses += 1;
            else bucket.draws += 1;
            if (axis) {
                axis.games += 1;
                if (result === 'win') axis.wins += 1;
                else if (result === 'lose') axis.losses += 1;
                else axis.draws += 1;
            }
        };

        matches.forEach((match) => {
            const result = match?.result;
            if (!result || !['win', 'lose', 'draw'].includes(result)) return;

            if (result === 'win') resultCounters.wins += 1;
            else if (result === 'lose') resultCounters.losses += 1;
            else resultCounters.draws += 1;

            const playDraw = (match?.playDraw || match?.play_draw || '').toLowerCase();
            if (playDraw === 'play') {
                playCounters.games += 1;
                if (result === 'win') playCounters.wins += 1;
                else if (result === 'lose') playCounters.losses += 1;
                else playCounters.draws += 1;
            } else if (playDraw === 'draw') {
                drawCounters.games += 1;
                if (result === 'win') drawCounters.wins += 1;
                else if (result === 'lose') drawCounters.losses += 1;
                else drawCounters.draws += 1;
            }

            const deckId = match?.myDeckId || match?.myDeckInfo?.id;
            if (deckId) {
                const deckName = match?.myDeckInfo?.name || 'Talia';
                const deckArch = match?.myDeckInfo?.archetypeInfo || null;
                const deckBucket = ensureBucket(perDeck, deckId, {
                    id: deckId,
                    name: deckName,
                    archetype: deckArch ? {
                        id: deckArch.id,
                        name: deckArch.name,
                        colors: deckArch.ink_colors || [],
                        ink_colors: deckArch.ink_colors || []
                    } : null
                });
                applyResult(
                    deckBucket,
                    result,
                    playDraw === 'play' ? deckBucket.play : playDraw === 'draw' ? deckBucket.draw : null
                );
            }

            const deckArchetype = match?.myDeckInfo?.archetypeInfo || null;
            const archetypeId = deckArchetype?.id;
            if (archetypeId) {
                const archetypeBucket = ensureBucket(perArchetype, archetypeId, {
                    id: archetypeId,
                    name: deckArchetype.name || 'Archetyp',
                    colors: deckArchetype.ink_colors || [],
                    ink_colors: deckArchetype.ink_colors || []
                });
                applyResult(
                    archetypeBucket,
                    result,
                    playDraw === 'play' ? archetypeBucket.play : playDraw === 'draw' ? archetypeBucket.draw : null
                );
            }
        });

        Object.values(perDeck).forEach((bucket) => {
            bucket.winrate = toWinrate(bucket.wins, bucket.losses);
            bucket.play.winrate = toWinrate(bucket.play.wins, bucket.play.losses);
            bucket.draw.winrate = toWinrate(bucket.draw.wins, bucket.draw.losses);
        });
        Object.values(perArchetype).forEach((bucket) => {
            bucket.winrate = toWinrate(bucket.wins, bucket.losses);
            bucket.play.winrate = toWinrate(bucket.play.wins, bucket.play.losses);
            bucket.draw.winrate = toWinrate(bucket.draw.wins, bucket.draw.losses);
        });

        const overallGames = resultCounters.wins + resultCounters.losses + resultCounters.draws;
        return {
            overall: {
                games: overallGames,
                wins: resultCounters.wins,
                losses: resultCounters.losses,
                draws: resultCounters.draws,
                winrate: toWinrate(resultCounters.wins, resultCounters.losses),
                play: {
                    games: playCounters.games,
                    wins: playCounters.wins,
                    losses: playCounters.losses,
                    draws: playCounters.draws,
                    winrate: toWinrate(playCounters.wins, playCounters.losses)
                },
                draw: {
                    games: drawCounters.games,
                    wins: drawCounters.wins,
                    losses: drawCounters.losses,
                    draws: drawCounters.draws,
                    winrate: toWinrate(drawCounters.wins, drawCounters.losses)
                }
            },
            perDeck,
            perArchetype
        };
    }, [matches]);

    const archetypeStats = stats?.perArchetype || {};
    const deckStats = stats?.perDeck || {};

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Ładowanie statystyk...
                </Typography>
            </Box>
        );
    }

    if (!stats) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Brak danych statystycznych
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(156, 163, 175, 0.7)', mt: 1 }}>
                    Zarejestruj mecze aby zobaczyć swoje statystyki
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Zakładki */}
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '12px',
                mb: 2
            }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant={isSmallMobile ? "scrollable" : "standard"}
                    scrollButtons={isSmallMobile ? "auto" : false}
                    sx={{
                        '& .MuiTabs-indicator': { backgroundColor: '#7c3aed' },
                        '& .MuiTab-root': {
                            color: 'rgba(196, 181, 253, 0.7)',
                            fontWeight: 600,
                            textTransform: 'none',
                            minHeight: isSmallMobile ? 44 : 52,
                            fontSize: isSmallMobile ? '0.75rem' : '0.8125rem',
                            py: 0,
                            '&.Mui-selected': { color: 'white' }
                        }
                    }}
                >
                    <Tab
                        label={
                            <Tooltip title="Wnioski, KPI, forma, seria i rekomendacje" placement="bottom">
                                <span>Podsumowanie</span>
                            </Tooltip>
                        }
                        icon={<EmojiEventsIcon sx={{ fontSize: isSmallMobile ? 24 : 28 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={
                            <Tooltip title="Statystyki per archetyp, winrate, Play/Draw" placement="bottom">
                                <span>Archetypy</span>
                            </Tooltip>
                        }
                        icon={<PaletteIcon sx={{ fontSize: isSmallMobile ? 24 : 28 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={
                            <Tooltip title="Statystyki per talia, winrate, Play/Draw" placement="bottom">
                                <span>Talie</span>
                            </Tooltip>
                        }
                        icon={<DeckIcon sx={{ fontSize: isSmallMobile ? 24 : 28 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={
                            <Tooltip title="Znajomość = gry danym archetypem. Sortowanie po popularności w mecie." placement="bottom">
                                <span>{isSmallMobile ? "Znajomość" : "Znajomość archetypów"}</span>
                            </Tooltip>
                        }
                        icon={<PsychologyIcon sx={{ fontSize: isSmallMobile ? 24 : 28 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={
                            <Tooltip title="Moja talia vs archetyp przeciwnika, favorable/unfavorable" placement="bottom">
                                <span>Matchupy</span>
                            </Tooltip>
                        }
                        icon={<SwapHorizIcon sx={{ fontSize: isSmallMobile ? 24 : 28 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={
                            <Tooltip title="Rolling winrate, forma, trendy miesięczne" placement="bottom">
                                <span>Trendy</span>
                            </Tooltip>
                        }
                        icon={<ShowChartIcon sx={{ fontSize: isSmallMobile ? 24 : 28 }} />}
                        iconPosition="start"
                    />
                </Tabs>
            </Card>

            {/* Zawartość zakładek */}
            {activeTab === 0 ? (
                <QuickInsightsDashboard stats={stats} matches={matches} loading={matchesLoading && matches.length === 0} />
            ) : activeTab === 1 ? (
                <ArchetypesDashboard archetypeStats={archetypeStats} formatWinrate={formatWinrate} getWinrateColor={getWinrateColor} isSmallMobile={isSmallMobile} navigate={navigate} />
            ) : activeTab === 2 ? (
                <DecksDashboard deckStats={deckStats} formatWinrate={formatWinrate} getWinrateColor={getWinrateColor} isSmallMobile={isSmallMobile} />
            ) : activeTab === 3 ? (
                <ArchetypeKnowledgePanel perArchetypeStats={archetypeStats} matches={matches} allArchetypes={allArchetypesForMatches} matrixVsCount={matrixVsCount} loading={loading} />
            ) : activeTab === 4 ? (
                <MatchupsDashboard matches={matches} allArchetypes={allArchetypesForMatches} formatWinrate={formatWinrate} getWinrateColor={getWinrateColor} isSmallMobile={isSmallMobile} navigate={navigate} />
            ) : (
                <TrendsDashboard matches={matches} allArchetypes={allArchetypesForMatches} formatWinrate={formatWinrate} getWinrateColor={getWinrateColor} isSmallMobile={isSmallMobile} />
            )}
        </Box>
    );
}

export default UserStatsPanel;
